import { JudgeType } from "@/types/conditionJudgeNodeType";

interface InfoNode {
  infos: Array<{
    id: string;
    name: string;
  }>;
}

interface SystemVar {
  value: string;
  label: string;
}

interface CustomVar {
  value: string;
  label: string;
}

interface InputVar {
  value: string;
  label: string;
}

interface FormatContentOptions {
  infoNodes?: InfoNode[];
  systemVars?: SystemVar[];
  customVars?: CustomVar[];
  inputVars?: InputVar[];
  returnWithBrackets?: boolean;
}

interface FormatContentResult {
  display: string; // 用于展示的格式化内容
  content: string; // 清理了无效变量后的原始内容
}

/**
 * 将内容中的变量替换为对应的标签名，同时清理无效变量
 * @param content - 包含变量的内容字符串
 * @param options - 配置选项
 * @returns 包含展示内容和清理后原始内容的结果对象
 */
export function formatContentWithLabels(
  content: string,
  {
    infoNodes = [],
    systemVars = [],
    customVars = [],
    inputVars = [],
    returnWithBrackets = true,
  }: FormatContentOptions,
): FormatContentResult {
  if (!content) return { display: "", content: "" };

  let displayContent = content;
  let cleanedContent = content;

  // 1. 处理信息变量
  const infoVarRegex = new RegExp(`\\$\\{${JudgeType.INFO}-([^}]+)\\}`, "g");
  displayContent = displayContent.replace(infoVarRegex, (match, infoId) => {
    for (const node of infoNodes) {
      const info = node.infos.find((info) => info.id === infoId);
      if (info) {
        return returnWithBrackets ? `[${info.name}]` : info.name;
      }
    }
    // 找不到匹配的信息,从cleanedContent中删除这个变量
    cleanedContent = cleanedContent.replace(match, "");
    return "";
  });

  // 2. 处理系统变量
  const sysVarRegex = new RegExp(`\\$\\{${JudgeType.SYSTEM}-([^}]+)\\}`, "g");
  displayContent = displayContent.replace(sysVarRegex, (match, value) => {
    const sysVar = systemVars.find((v) => v.value === value);
    if (sysVar) {
      return returnWithBrackets ? `[${sysVar.label}]` : sysVar.label;
    }
    // 找不到匹配的系统变量,从cleanedContent中删除
    cleanedContent = cleanedContent.replace(match, "");
    return "";
  });

  // 3. 处理自定义变量
  const customVarRegex = new RegExp(
    `\\$\\{${JudgeType.CUSTOM}-([^}]+)\\}`,
    "g",
  );
  displayContent = displayContent.replace(customVarRegex, (match, value) => {
    const customVar = customVars.find((v) => v.value === value);
    if (customVar) {
      return returnWithBrackets ? `[${customVar.label}]` : customVar.label;
    }
    // 找不到匹配的自定义变量,从cleanedContent中删除
    cleanedContent = cleanedContent.replace(match, "");
    return "";
  });

  // 4. 处理启动节点变量
  const inputVarRegex = new RegExp(
    `\\$\\{${JudgeType.INPUT}-([^}]+)\\}`,
    "g",
  );
  displayContent = displayContent.replace(inputVarRegex, (match, value) => {
    const inputVar = inputVars.find((v) => v.value === value);
    if (inputVar) {
      return returnWithBrackets ? `[${inputVar.label}]` : inputVar.label;
    }
    cleanedContent = cleanedContent.replace(match, "");
    return "";
  });

  // 5. 清理连续空格
  displayContent = displayContent.replace(/\s+/g, " ").trim();
  cleanedContent = cleanedContent.replace(/\s+/g, " ").trim();

  // 6. 保留格式为 ${type-} 的无效变量
  const invalidVarRegex = /\$\{[^}]*-\}/g;
  const invalidVars = content.match(invalidVarRegex) || [];
  for (const invalidVar of invalidVars) {
    if (cleanedContent.includes(invalidVar)) {
      // 如果是 ${type-} 格式的无效变量，保留在cleanedContent中
      continue;
    }
  }

  return {
    // display 要去掉${x-}
    display: displayContent.replace(/\$\{[^}]*-\}/g, "").trim(),
    content: cleanedContent,
  };
}
